import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import type { LabeledValuePayload } from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentsFetched';
import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import UsageAssignmentCardActions from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard/UsageAssignmentCardActions';
import type AiModelsListView from '@UI/Store/AiConfiguration/Views/AiModelsListView';
import type ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import type UsageSettingsView from '@UI/Store/AiConfiguration/Views/UsageSettingsView';
import { css } from '@emotion/react';
import * as React from 'react';
import UsageAssignmentHeaderCard from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard/UsageAssignmentHeaderCard';

/**
 * The three render branches below share the same four tracks — header, primary line, secondary line,
 * actions row — always in that order, an empty `<div />` standing in for a track a branch has nothing
 * to say in. A track can still hold several stacked paragraphs (the assigned branch's secondary line
 * does): only the *number of tracks* is fixed, not the amount of content each one carries.
 */
const CARD_TRACK_COUNT = 4;

/** Row spacing shared by the three render branches, so the three cannot drift apart. */
const CARD_ROW_GAP = '0.75rem';

const lineGroupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const modelNameStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const statusStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const warningBlockStyle = css`
	margin: 0;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

/** What becomes impossible when no model resolves for this usage, at any level. */
const UNAVAILABLE_USAGE_MESSAGES: Record<string, string> = {
	interview_transcription: "La transcription d'entretien ne pourra pas s'exécuter.",
	structured_extraction: "L'extraction de données structurées ne pourra pas s'exécuter.",
	conversation: "La conversation ne pourra pas s'exécuter.",
	dictation: "La dictée ne pourra pas s'exécuter.",
};

const unavailableMessageFor = (usage: { readonly value: string; readonly label: string }): string =>
	UNAVAILABLE_USAGE_MESSAGES[usage.value] ?? `${usage.label} ne pourra pas s'exécuter.`;

/** `effectiveLevel.value` is `'account' | 'organization' | 'facility'`: gendered phrasing cannot be
 * derived from `effectiveLevel.label` ("Compte" / "Structure" / "Agence") without sounding wrong. */
const INHERITED_FROM_PHRASES: Record<string, string> = {
	account: 'Hérité du compte.',
	organization: 'Hérité de la structure.',
	facility: "Hérité de l'agence.",
};

/** Lower-cased, mid-sentence form of `INHERITED_FROM_PHRASES`, for `describeCallSettings` below. */
const originPhraseFor = (level: LabeledValuePayload): string => {
	const phrase = INHERITED_FROM_PHRASES[level.value] ?? `Hérité de ${level.label}.`;
	return phrase.charAt(0).toLowerCase() + phrase.slice(1);
};

/**
 * One line summarising the call settings the server actually resolved for this usage — driven
 * entirely by `applicable`/`effective`/`effectiveLevel`, never by a re-derived audio/text usage rule.
 * Only the settings that carry a value are mentioned; an empty override (`[]`/`''`) still counts as a
 * value, since the server already tells apart "nobody decided" (`effective === null`) from "decided
 * empty" via `effectiveLevel`.
 */
const describeCallSettings = (settings: UsageSettingsView): string => {
	const fragments: string[] = [];

	if (settings.vocabularyTerms.applicable && settings.vocabularyTerms.effective !== null) {
		const count = settings.vocabularyTerms.effective.length;
		const origin =
			settings.vocabularyTerms.effectiveLevel !== null
				? originPhraseFor(settings.vocabularyTerms.effectiveLevel)
				: null;
		fragments.push(`Vocabulaire : ${count} terme${count > 1 ? 's' : ''}${origin ? `, ${origin}` : '.'}`);
	}

	if (
		settings.contextPrompt.applicable &&
		settings.contextPrompt.effective !== null &&
		settings.contextPrompt.effective !== ''
	) {
		const origin =
			settings.contextPrompt.effectiveLevel !== null
				? originPhraseFor(settings.contextPrompt.effectiveLevel)
				: null;
		fragments.push(`Contexte personnalisé${origin ? `, ${origin}` : '.'}`);
	}

	if (
		settings.systemPrompt.applicable &&
		settings.systemPrompt.effective !== null &&
		settings.systemPrompt.effective !== ''
	) {
		const origin =
			settings.systemPrompt.effectiveLevel !== null
				? originPhraseFor(settings.systemPrompt.effectiveLevel)
				: null;
		fragments.push(`Instruction système personnalisée${origin ? `, ${origin}` : '.'}`);
	}

	if (settings.temperature.applicable && settings.temperature.effective !== null) {
		const origin =
			settings.temperature.effectiveLevel !== null
				? originPhraseFor(settings.temperature.effectiveLevel)
				: null;
		fragments.push(`Température : ${settings.temperature.effective}${origin ? `, ${origin}` : '.'}`);
	}

	if (fragments.length === 0) {
		return 'Aucun réglage d’appel.';
	}

	return fragments.join(' ');
};

type OptionalUsagePresentation = {
	readonly unassignedNotice: string;
	readonly unassignedDetail: string;
	readonly assignedNote: string;
};

/**
 * Usages whose empty affectation is a normal state, not a missing configuration: an upstream model
 * already covers the capability, so nothing blocks. A usage absent from this table is required — its
 * empty state keeps the "to configure" presentation below.
 */
const OPTIONAL_USAGE_PRESENTATIONS: Record<string, OptionalUsagePresentation> = {
	speaker_diarization: {
		unassignedNotice: 'Aucun second moteur.',
		unassignedDetail: 'Le moteur de transcription sépare déjà les locuteurs.',
		assignedNote: 'La transcription est reprise par ce moteur pour séparer les locuteurs.',
	},
};

type Props = {
	readonly assignment: ModelAssignmentView;
	readonly scope: AssignmentScope;
	/** "ce compte" / "cette structure" / "cette agence": names the active tab's own level. */
	readonly scopeLabel: string;
	readonly models: AiModelsListView;
};

/**
 * One usage (transcription, extraction, conversation, dictation, speaker diarization), always
 * rendered even when unassigned — the server always answers with the five entries. Reads
 * `ownModelId` and `effectiveModelId` as bare numbers (never `{ id }` relations) and resolves them
 * against the already-loaded catalogue. The dropdown (`UsageAssignmentCardActions`) renders in every
 * state, unconfigured included — except for a live usage, where it only shows once the live switch
 * is on. Most usages are required — an unresolved one is a defect, flagged by the "À configurer"
 * tag; `OPTIONAL_USAGE_PRESENTATIONS` lists the usages whose empty state is normal instead.
 */
const UsageAssignmentCard: React.FC<Props> = ({ assignment, scope, scopeLabel, models }) => {
	const ownModel = React.useMemo(
		() => models.findById(assignment.ownModelId),
		[models, assignment.ownModelId],
	);
	const effectiveModel = React.useMemo(
		() => models.findById(assignment.effectiveModelId),
		[models, assignment.effectiveModelId],
	);
	const displayedModel = ownModel ?? effectiveModel;
	const optionalPresentation = OPTIONAL_USAGE_PRESENTATIONS[assignment.usage.value];

	if (assignment.isUnconfigured) {
		if (optionalPresentation !== undefined) {
			return (
				// An unassigned optional usage is a normal state, not a defect: the hairline stays neutral
				// even though `isUnconfigured` is true.
				<GridCard trackCount={CARD_TRACK_COUNT} rowGap={CARD_ROW_GAP} tone="default">
					<UsageAssignmentHeaderCard
						assignment={assignment}
						scope={scope}
						optionalPresentation={!!optionalPresentation}
					/>
					<p css={statusStyle}>{optionalPresentation.unassignedNotice}</p>
					<p css={statusStyle}>{optionalPresentation.unassignedDetail}</p>
					<UsageAssignmentCardActions assignment={assignment} scope={scope} models={models} />
				</GridCard>
			);
		}

		return (
			<GridCard trackCount={CARD_TRACK_COUNT} rowGap={CARD_ROW_GAP} tone="warning">
				<UsageAssignmentHeaderCard
					assignment={assignment}
					scope={scope}
					optionalPresentation={false}
				/>
				<p css={statusStyle}>Aucun moteur configuré.</p>
				<p css={statusStyle}>{unavailableMessageFor(assignment.usage)}</p>
				<UsageAssignmentCardActions assignment={assignment} scope={scope} models={models} />
			</GridCard>
		);
	}

	return (
		<GridCard trackCount={CARD_TRACK_COUNT} rowGap={CARD_ROW_GAP}>
			<UsageAssignmentHeaderCard
				assignment={assignment}
				scope={scope}
				optionalPresentation={!!optionalPresentation}
			/>
			<p css={modelNameStyle}>{displayedModel?.label}</p>
			<div css={lineGroupStyle}>
				{assignment.ownModelId !== null ? (
					<p css={statusStyle}>Défini pour {scopeLabel}.</p>
				) : (
					<p css={statusStyle}>
						{assignment.effectiveLevel !== null
							? (INHERITED_FROM_PHRASES[assignment.effectiveLevel.value] ??
								`Hérité de ${assignment.effectiveLevel.label}.`)
							: null}
					</p>
				)}
				{optionalPresentation !== undefined ? (
					<p css={statusStyle}>{optionalPresentation.assignedNote}</p>
				) : null}
				{effectiveModel !== null && effectiveModel.lastTestSucceeded === false ? (
					<p css={warningBlockStyle}>
						Le dernier appel a échoué
						{effectiveModel.lastTestMessage ? ` : ${effectiveModel.lastTestMessage}` : '.'}
					</p>
				) : null}
				<p css={statusStyle}>{describeCallSettings(assignment.settings)}</p>
				{assignment.settings.hasAnyConflict ? (
					<p css={warningBlockStyle}>
						Un réglage d’appel est enregistré mais n’est plus accepté par le moteur affecté. Il ne
						sera pas envoyé.
					</p>
				) : null}
			</div>
			<UsageAssignmentCardActions assignment={assignment} scope={scope} models={models} />
		</GridCard>
	);
};

export default UsageAssignmentCard;
